import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SyncToProdButton from '../components/SyncToProdButton';
import { invalidateCache } from '../lib/usePublicData';
import {
  fetchItems, updateItem, createItem, deleteItem, uploadImage,
  fetchAdminArtists, updateArtist, createArtist, deleteArtist,
  renderInSitu, fetchSubscribers, deleteSubscriber, sendNewsletter,
  translateToFr, regenerateSitemap, fetchInstagramAdmin, saveInstagram,
  fetchPromoVideoAdmin, savePromoVideo,
} from '../lib/api';

const EMPTY_GALLERY = {
  title: '', artist: '', year: '', size: '', price: '',
  image: '', imageContext: null, category: 'Painting',
  featured: false, sold: false, description: '',
};
const EMPTY_ANTIQUE = {
  title: '', period: '', price: '', image: '', imageContext: null,
  description: '', origin: 'France', kind: 'antique', sold: false, featured: false,
};
const EMPTY_ARTIST = { slug: '', name: '', shortBio: '', bio: '' };

const Field = ({ label, value, onChange, type = 'text', textarea = false, testid }) => (
  <label className="block mb-3">
    <span className="text-xs uppercase tracking-wider text-dark-green/70">{label}</span>
    {textarea ? (
      <textarea
        data-testid={testid}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full mt-1 p-2 bg-cream border-2 border-gold/30 focus:border-dark-crimson outline-none text-dark-green"
      />
    ) : (
      <input
        data-testid={testid}
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 p-2 bg-cream border-2 border-gold/30 focus:border-dark-crimson outline-none text-dark-green"
      />
    )}
  </label>
);

const ImageUploadField = ({ label, value, onChange, testid }) => {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const { data } = await uploadImage(f);
      onChange(data.path);
    } catch (err) {
      alert('Upload failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mb-3">
      <div className="text-xs uppercase tracking-wider text-dark-green/70 mb-1">{label}</div>
      {value && (
        <img src={value} alt="" className="h-32 object-contain bg-cream border border-gold/30 mb-2" />
      )}
      <input
        data-testid={testid}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="/image-path.png  or upload below"
        className="w-full p-2 mb-2 bg-cream border-2 border-gold/30 outline-none text-dark-green text-sm"
      />
      <input type="file" accept="image/*" onChange={handle} disabled={busy} className="text-sm" />
      {busy && <span className="text-sm text-dark-crimson ml-2">Uploading…</span>}
    </div>
  );
};

const AutoInSituButton = ({ kind, item, onRendered }) => {
  const [mode, setMode] = useState('painting');
  const [busy, setBusy] = useState(false);
  const ready = !!item.id && !!item.image;
  const click = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      const { data } = await renderInSitu({
        type: kind === 'gallery' ? 'gallery' : 'antique',
        id: item.id,
        mode,
      });
      onRendered(data.imageContext);
    } catch (err) {
      alert('In-situ render failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-2 p-3 border-2 border-dashed border-gold/40 bg-cream/40">
      <div className="text-xs uppercase tracking-wider text-dark-green/70 mb-2">
        Auto-generate canonical in-situ
      </div>
      {!ready && (
        <div className="text-xs text-dark-crimson mb-2">
          Save the item with a catalogue image first, then come back to use this.
        </div>
      )}
      <div className="flex items-center gap-3">
        <label className="text-sm text-dark-green flex items-center gap-1">
          <input
            type="radio"
            name={`mode-${item.id || 'new'}`}
            value="painting"
            checked={mode === 'painting'}
            onChange={() => setMode('painting')}
          />
          Painting (framed on green wall)
        </label>
        <label className="text-sm text-dark-green flex items-center gap-1">
          <input
            type="radio"
            name={`mode-${item.id || 'new'}`}
            value="sculpture"
            checked={mode === 'sculpture'}
            onChange={() => setMode('sculpture')}
          />
          Sculpture (free-standing on console)
        </label>
      </div>
      <button
        type="button"
        data-testid="auto-insitu-btn"
        onClick={click}
        disabled={!ready || busy}
        className="mt-3 px-4 py-2 bg-rich-green text-cream text-sm tracking-wider disabled:opacity-50"
      >
        {busy ? 'Rendering… (≈ 30s)' : 'Generate in-situ image'}
      </button>
    </div>
  );
};

const ItemEditor = ({ item, kind, onSave, onCancel, onDelete }) => {
  const [draft, setDraft] = useState(item);
  const [translating, setTranslating] = useState(false);
  const isNew = !item.id;
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const doTranslate = async () => {
    if (!draft.title && !draft.description) return;
    setTranslating(true);
    try {
      const { data } = await translateToFr({ title: draft.title, description: draft.description });
      setDraft((d) => ({ ...d, title_fr: data.title_fr, description_fr: data.description_fr }));
    } catch (err) {
      alert('Translation failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setTranslating(false);
    }
  };
  return (
    <div className="bg-ivory border-2 border-gold p-6 mb-6 shadow-xl">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-2xl text-dark-green" style={{ fontFamily: 'Playfair Display, serif' }}>
          {isNew ? 'New item' : draft.title}
        </h3>
        <div className="flex gap-2">
          {!isNew && (
            <button
              data-testid="delete-item"
              onClick={() => { if (window.confirm('Delete this item?')) onDelete(); }}
              className="px-3 py-1 text-sm border border-dark-crimson text-dark-crimson hover:bg-dark-crimson hover:text-cream transition-colors"
            >
              Delete
            </button>
          )}
          <button onClick={onCancel} className="px-3 py-1 text-sm border border-dark-green text-dark-green">
            Cancel
          </button>
          <button
            data-testid="save-item"
            onClick={() => onSave(draft)}
            className="px-4 py-1 bg-dark-crimson text-cream tracking-wider"
          >
            Save
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <Field label="Title" value={draft.title} onChange={(v) => set('title', v)} testid="field-title" />
          {kind === 'gallery' ? (
            <>
              <Field label="Artist" value={draft.artist} onChange={(v) => set('artist', v)} testid="field-artist" />
              <Field label="Year" value={draft.year} onChange={(v) => set('year', v)} />
              <Field label="Size" value={draft.size} onChange={(v) => set('size', v)} />
              <Field label="Category" value={draft.category} onChange={(v) => set('category', v)} />
            </>
          ) : (
            <>
              <Field label="Period" value={draft.period} onChange={(v) => set('period', v)} />
              <Field label="Origin" value={draft.origin} onChange={(v) => set('origin', v)} />
              <label className="block mb-3">
                <span className="text-xs uppercase tracking-wider text-dark-green/70">Kind</span>
                <select
                  value={draft.kind || 'antique'}
                  onChange={(e) => set('kind', e.target.value)}
                  className="w-full mt-1 p-2 bg-cream border-2 border-gold/30 text-dark-green"
                >
                  <option value="antique">Antique</option>
                  <option value="acquisition">Acquisition</option>
                </select>
              </label>
            </>
          )}
          <Field label="Price" value={draft.price} onChange={(v) => set('price', v)} testid="field-price" />
          <Field label="Description" value={draft.description} onChange={(v) => set('description', v)} textarea />

          {/* French content (bilingual) */}
          <div className="mt-2 p-3 border-2 border-dashed border-rich-green/40 bg-cream/40">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wider text-rich-green">Français (FR)</span>
              <button
                type="button"
                onClick={doTranslate}
                disabled={translating || (!draft.title && !draft.description)}
                data-testid="translate-fr-btn"
                className="px-3 py-1 bg-rich-green text-cream text-xs tracking-wider disabled:opacity-50"
              >
                {translating ? 'Translating…' : '✦ Translate to FR'}
              </button>
            </div>
            <Field label="Titre (FR)" value={draft.title_fr} onChange={(v) => set('title_fr', v)} testid="field-title-fr" />
            <Field label="Description (FR)" value={draft.description_fr} onChange={(v) => set('description_fr', v)} textarea />
          </div>
        </div>
        <div>
          <ImageUploadField label="Catalogue image" value={draft.image} onChange={(v) => set('image', v)} testid="field-image" />
          <ImageUploadField label="In-situ / hover image (optional)" value={draft.imageContext} onChange={(v) => set('imageContext', v)} />
          <AutoInSituButton
            kind={kind}
            item={draft}
            onRendered={(path) => set('imageContext', path)}
          />
          <div className="flex gap-6 mt-4">
            <label className="flex items-center gap-2 text-dark-green">
              <input
                data-testid="toggle-featured"
                type="checkbox"
                checked={!!draft.featured}
                onChange={(e) => set('featured', e.target.checked)}
              />
              Featured
            </label>
            <label className="flex items-center gap-2 text-dark-green">
              <input
                data-testid="toggle-sold"
                type="checkbox"
                checked={!!draft.sold}
                onChange={(e) => set('sold', e.target.checked)}
              />
              Sold
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};


const NewsletterPanel = () => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await fetchSubscribers();
      setSubs(data);
    } catch (err) {
      alert('Failed to load subscribers: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const removeOne = async (id, email) => {
    if (!window.confirm(`Remove ${email} from the list?`)) return;
    try {
      await deleteSubscriber(id);
      setSubs((cur) => cur.filter((s) => s.id !== id));
    } catch (err) {
      alert('Delete failed: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) return;
    const active = subs.filter((s) => s.status === 'active').length;
    if (!window.confirm(`Send this newsletter to ${active} active subscribers?`)) return;
    setSending(true);
    setResult(null);
    try {
      const { data } = await sendNewsletter({ subject, html: body });
      setResult(data);
      if (data.ok) { setSubject(''); setBody(''); }
    } catch (err) {
      setResult({ ok: false, message: err?.response?.data?.detail || err.message });
    } finally {
      setSending(false);
    }
  };

  const active = subs.filter((s) => s.status === 'active');
  const unsubbed = subs.filter((s) => s.status !== 'active');

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      {/* Left — Subscribers list */}
      <div className="bg-ivory border-2 border-gold/40 p-6">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="text-2xl text-dark-green" style={{ fontFamily: 'Playfair Display, serif' }}>
            Subscribers
          </h3>
          <div className="text-sm text-dark-green/70">
            {loading ? 'loading…' : `${active.length} active · ${unsubbed.length} unsubscribed`}
          </div>
        </div>
        {!loading && subs.length === 0 && (
          <p className="text-dark-green/60 italic py-8 text-center">
            No subscribers yet. The signup form on the homepage will add them here.
          </p>
        )}
        <ul className="divide-y divide-gold/20 max-h-[520px] overflow-y-auto">
          {subs.map((s) => (
            <li key={s.id} className="flex items-center justify-between py-2 text-sm" data-testid={`sub-${s.id}`}>
              <div>
                <div className="text-dark-green">{s.email}</div>
                <div className="text-dark-green/50 text-xs">
                  {(s.subscribed_at || '').slice(0, 10)} · {s.lang?.toUpperCase() || 'EN'}
                  {s.status !== 'active' && (
                    <span className="ml-2 text-dark-crimson uppercase tracking-wider">unsubscribed</span>
                  )}
                </div>
              </div>
              <button
                onClick={() => removeOne(s.id, s.email)}
                className="text-dark-crimson hover:underline text-xs"
              >
                delete
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Right — Compose */}
      <div className="bg-ivory border-2 border-gold/40 p-6">
        <h3 className="text-2xl text-dark-green mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          Compose newsletter
        </h3>

        <label className="block text-xs uppercase tracking-wider text-dark-green/70 mb-1">Subject</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Autumn arrivals at The Chimera Gallery"
          className="w-full border border-dark-green/40 bg-cream px-3 py-2 mb-4"
          data-testid="newsletter-subject"
        />

        <label className="block text-xs uppercase tracking-wider text-dark-green/70 mb-1">
          Body (HTML supported — use &lt;p&gt;, &lt;h2&gt;, &lt;a href&gt;, &lt;img src&gt; etc.)
        </label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={14}
          placeholder="<p>Dear friends of The Chimera,</p>

<p>This month we've added a few pieces we think you'll enjoy…</p>

<p><a href='https://chimera-gallery.com/gallery'>Browse the new arrivals →</a></p>

<p>— Dave</p>"
          className="w-full border border-dark-green/40 bg-cream px-3 py-2 mb-4 font-mono text-sm"
          data-testid="newsletter-body"
        />

        <div className="flex items-center justify-between">
          <p className="text-xs text-dark-green/60 max-w-xs">
            An unsubscribe link is automatically added to every email.
          </p>
          <button
            onClick={send}
            disabled={sending || !subject.trim() || !body.trim()}
            data-testid="newsletter-send"
            className="px-6 py-3 bg-dark-crimson text-cream tracking-wider text-sm border border-gold disabled:opacity-50"
          >
            {sending ? 'Sending…' : `Send to ${active.length}`}
          </button>
        </div>

        {result && (
          <div className={`mt-4 p-3 text-sm ${result.ok ? 'bg-rich-green/10 border border-rich-green text-rich-green' : 'bg-dark-crimson/10 border border-dark-crimson text-dark-crimson'}`}>
            {result.ok
              ? `✔ Sent to ${result.sent} recipient${result.sent === 1 ? '' : 's'}${result.failed ? ` (${result.failed} failed)` : ''}.`
              : `✖ ${result.message || 'Send failed.'}`}
          </div>
        )}
      </div>
    </div>
  );
};


const InstagramPanel = () => {
  const [handle, setHandle] = useState('chimera_part_2');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await fetchInstagramAdmin();
        setHandle(data.handle || 'chimera_part_2');
        setPosts(data.posts || []);
      } catch (_) { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);

  const setPost = (i, k, v) => setPosts((ps) => ps.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  const addPost = () => setPosts((ps) => (ps.length >= 6 ? ps : [...ps, { image: '', url: '' }]));
  const removePost = (i) => setPosts((ps) => ps.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await saveInstagram({ handle, posts: posts.filter((p) => p.image) });
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      alert('Save failed: ' + (err?.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-dark-green/60 py-8">Loading…</div>;

  return (
    <div className="bg-ivory border-2 border-gold/40 p-6 max-w-4xl">
      <h3 className="text-2xl text-dark-green mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
        Instagram feed (homepage)
      </h3>
      <p className="text-sm text-dark-green/70 mb-6">
        Add up to 6 images to show a curated “From Our Instagram” grid on the homepage. Each links to your profile (or paste a specific post URL). Leave empty to show just a “Follow” button.
      </p>

      <label className="block mb-6 max-w-xs">
        <span className="text-xs uppercase tracking-wider text-dark-green/70">Instagram handle</span>
        <div className="flex items-center mt-1 border-2 border-gold/30 bg-cream">
          <span className="px-2 text-dark-green/50">@</span>
          <input
            data-testid="instagram-handle"
            value={handle}
            onChange={(e) => setHandle(e.target.value.replace('@', ''))}
            className="w-full p-2 bg-cream outline-none text-dark-green"
          />
        </div>
      </label>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {posts.map((p, i) => (
          <div key={i} className="border-2 border-gold/30 p-3 bg-cream/40" data-testid={`instagram-post-editor-${i}`}>
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs uppercase tracking-wider text-dark-green/60">Post {i + 1}</span>
              <button onClick={() => removePost(i)} className="text-dark-crimson text-xs hover:underline">remove</button>
            </div>
            <ImageUploadField label="Image" value={p.image} onChange={(v) => setPost(i, 'image', v)} testid={`instagram-img-${i}`} />
            <input
              value={p.url || ''}
              onChange={(e) => setPost(i, 'url', e.target.value)}
              placeholder="Post URL (optional)"
              className="w-full p-2 bg-cream border-2 border-gold/30 outline-none text-dark-green text-sm"
            />
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        {posts.length < 6 && (
          <button onClick={addPost} data-testid="instagram-add" className="px-4 py-2 border-2 border-dark-green text-dark-green text-sm tracking-wider hover:bg-dark-green hover:text-cream transition-colors">
            + Add image
          </button>
        )}
        <button onClick={save} disabled={saving} data-testid="instagram-save" className="px-6 py-2 bg-dark-crimson text-cream text-sm tracking-wider border border-gold disabled:opacity-50">
          {saving ? 'Saving…' : 'Save Instagram feed'}
        </button>
        {saved && <span className="text-rich-green text-sm">✔ Saved</span>}
      </div>
    </div>
  );
};


const VideoPanel = () => {
  const [url, setUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await fetchPromoVideoAdmin();
        setUrl(data.url || '');
        setCaption(data.caption || '');
        setEnabled(!!data.enabled);
      } catch (_) { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await savePromoVideo({ url, enabled, caption });
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      alert('Save failed: ' + (err?.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-dark-green/60 py-8">Loading…</div>;

  return (
    <div className="bg-ivory border-2 border-gold/40 p-6 max-w-2xl">
      <h3 className="text-2xl text-dark-green mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
        Promo film (homepage “Watch the Film” button)
      </h3>
      <p className="text-sm text-dark-green/70 mb-6">
        Paste a <strong>YouTube</strong> or <strong>Vimeo</strong> link (recommended), or a direct video file URL.
        The “Watch the Film” button on the homepage stays hidden until you tick “Show on site”.
      </p>

      <label className="block mb-4">
        <span className="text-xs uppercase tracking-wider text-dark-green/70">Video link</span>
        <input
          data-testid="promo-video-url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.youtube.com/watch?v=…"
          className="w-full mt-1 p-3 bg-cream border-2 border-gold/30 outline-none text-dark-green"
        />
      </label>

      <label className="block mb-4">
        <span className="text-xs uppercase tracking-wider text-dark-green/70">Caption (optional — shows under the button)</span>
        <input
          data-testid="promo-video-caption-input"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Our Mullingar re-launch — the French adventure comes next"
          className="w-full mt-1 p-3 bg-cream border-2 border-gold/30 outline-none text-dark-green"
        />
      </label>

      <label className="flex items-center gap-3 mb-6 cursor-pointer">
        <input
          type="checkbox"
          data-testid="promo-video-enabled"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="w-5 h-5 accent-dark-crimson"
        />
        <span className="text-dark-green">Show the “Watch the Film” button on the homepage</span>
      </label>

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving} data-testid="promo-video-save" className="px-6 py-2 bg-dark-crimson text-cream text-sm tracking-wider border border-gold disabled:opacity-50">
          {saving ? 'Saving…' : 'Save video'}
        </button>
        {saved && <span className="text-rich-green text-sm">✔ Saved</span>}
      </div>
    </div>
  );
};


const AdminDashboard = () => {
  const { user, ready, logout } = useAuth();
  const [tab, setTab] = useState('gallery');
  const [items, setItems] = useState([]);
  const [artists, setArtists] = useState([]);
  const [editing, setEditing] = useState(null);   // item or artist being edited
  const [editKind, setEditKind] = useState(null);  // 'gallery' | 'antique' | 'artist'

  const reload = async () => {
    if (tab === 'newsletter' || tab === 'instagram' || tab === 'video') return;
    if (tab === 'artists') {
      const { data } = await fetchAdminArtists();
      setArtists(data);
    } else {
      const { data } = await fetchItems(tab);
      setItems(data);
    }
  };

  useEffect(() => { if (user) reload(); /* eslint-disable-next-line */ }, [tab, user]);

  if (!ready) return <div className="p-12 text-dark-green">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const save = async (draft) => {
    try {
      if (editKind === 'artist') {
        if (draft._isNew) {
          delete draft._isNew;
          await createArtist(draft);
        } else {
          await updateArtist(draft.slug, draft);
        }
      } else {
        const t = editKind;
        if (draft.id) await updateItem(t, draft.id, draft);
        else await createItem(t, draft);
      }
      setEditing(null); setEditKind(null);
      invalidateCache();
      reload();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  const remove = async () => {
    if (editKind === 'artist') await deleteArtist(editing.slug);
    else await deleteItem(editKind, editing.id);
    setEditing(null); setEditKind(null);
    reload();
  };

  const openNew = () => {
    if (tab === 'artists') { setEditing({ ...EMPTY_ARTIST, _isNew: true }); setEditKind('artist'); }
    else if (tab === 'gallery') { setEditing({ ...EMPTY_GALLERY }); setEditKind('gallery'); }
    else { setEditing({ ...EMPTY_ANTIQUE }); setEditKind('antique'); }
  };

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-ivory border-b-2 border-gold px-6 py-4 flex justify-between items-center sticky top-0 z-40">
        <div>
          <h1 className="text-2xl text-dark-green tracking-widest" style={{ fontFamily: 'Playfair Display, serif' }}>
            CHIMERA · ADMIN
          </h1>
          <p className="text-xs text-dark-green/60">Signed in as {user.username}</p>
        </div>
        <div className="flex gap-3 items-center">
          <SyncToProdButton username={user.username} />
          <button
            onClick={async () => {
              try {
                const { data } = await regenerateSitemap();
                alert(`Sitemap regenerated — ${data.urls} URLs. Re-deploy changes to publish it.`);
              } catch (err) {
                alert('Sitemap failed: ' + (err?.response?.data?.detail || err.message));
              }
            }}
            data-testid="regenerate-sitemap"
            className="text-sm px-3 py-1 border border-rich-green text-rich-green hover:bg-rich-green hover:text-cream transition-colors"
          >
            Regenerate Sitemap
          </button>
          <Link to="/" className="text-sm text-dark-green hover:text-dark-crimson">View site →</Link>
          <button onClick={logout} data-testid="logout" className="px-3 py-1 text-sm border border-dark-crimson text-dark-crimson hover:bg-dark-crimson hover:text-cream transition-colors">
            Log out
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        <div className="flex gap-0 border-b-2 border-gold/40 mb-8">
          {[['gallery', 'Gallery (Featured Collection)'], ['antique', 'Antiques & Acquisitions'], ['artists', 'Artists'], ['newsletter', 'Newsletter'], ['instagram', 'Instagram'], ['video', 'Video']].map(([k, label]) => (
            <button
              key={k}
              data-testid={`admin-tab-${k}`}
              onClick={() => { setTab(k); setEditing(null); }}
              className={`px-6 py-3 -mb-1 border-b-4 transition-colors ${
                tab === k ? 'border-dark-crimson text-dark-green' : 'border-transparent text-dark-green/50 hover:text-dark-green'
              }`}
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {label}
            </button>
          ))}
          {tab !== 'newsletter' && tab !== 'instagram' && tab !== 'video' && (
            <button
              onClick={openNew}
              data-testid="add-new"
              className="ml-auto mb-2 px-4 py-2 bg-dark-green text-cream tracking-wider text-sm hover:bg-dark-crimson transition-colors"
            >
              + Add new
            </button>
          )}
        </div>

        {editing && (
          editKind === 'artist' ? (
            <div className="bg-ivory border-2 border-gold p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-2xl text-dark-green" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {editing._isNew ? 'New artist' : editing.name}
                </h3>
                <div className="flex gap-2">
                  {!editing._isNew && (
                    <button onClick={() => { if (window.confirm('Delete this artist?')) remove(); }} className="px-3 py-1 text-sm border border-dark-crimson text-dark-crimson">Delete</button>
                  )}
                  <button onClick={() => setEditing(null)} className="px-3 py-1 text-sm border border-dark-green text-dark-green">Cancel</button>
                  <button onClick={() => save(editing)} className="px-4 py-1 bg-dark-crimson text-cream">Save</button>
                </div>
              </div>
              <Field label="Slug (URL, e.g. mark-bailey)" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} />
              <Field label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
              <ImageUploadField
                label="Portrait image"
                value={editing.photo}
                onChange={(v) => setEditing({ ...editing, photo: v })}
                testid="field-artist-photo"
              />
              <Field label="Short bio" value={editing.shortBio} onChange={(v) => setEditing({ ...editing, shortBio: v })} textarea />
              <Field label="Full bio" value={editing.bio} onChange={(v) => setEditing({ ...editing, bio: v })} textarea />
            </div>
          ) : (
            <ItemEditor
              item={editing}
              kind={editKind}
              onSave={save}
              onCancel={() => setEditing(null)}
              onDelete={remove}
            />
          )
        )}

        {tab === 'newsletter' ? (
          <NewsletterPanel />
        ) : tab === 'instagram' ? (
          <InstagramPanel />
        ) : tab === 'video' ? (
          <VideoPanel />
        ) : tab === 'artists' ? (
          <div className="grid md:grid-cols-3 gap-4">
            {artists.map((a) => (
              <button
                key={a.slug}
                onClick={() => { setEditing(a); setEditKind('artist'); }}
                className="bg-ivory border border-gold/40 p-4 text-left hover:border-dark-crimson"
              >
                <div className="text-lg text-dark-green" style={{ fontFamily: 'Playfair Display, serif' }}>{a.name}</div>
                <div className="text-sm text-dark-green/60">{a.slug}</div>
              </button>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((it) => (
              <button
                key={it.id}
                data-testid={`item-${it.id}`}
                onClick={() => { setEditing(it); setEditKind(tab); }}
                className="bg-ivory border border-gold/40 hover:border-dark-crimson text-left overflow-hidden"
              >
                <div className="h-40 bg-cream flex items-center justify-center overflow-hidden">
                  {it.image ? <img src={it.image} alt="" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-dark-green/40">no image</span>}
                </div>
                <div className="p-3">
                  <div className="text-base text-dark-green" style={{ fontFamily: 'Playfair Display, serif' }}>{it.title}</div>
                  <div className="text-xs text-dark-green/60">{it.artist || it.period}</div>
                  <div className="text-sm text-dark-crimson mt-1">{it.price}</div>
                  <div className="flex gap-2 mt-2 text-[10px] uppercase tracking-wider">
                    {it.featured && <span className="bg-gold/30 text-dark-green px-2 py-0.5">Featured</span>}
                    {it.sold && <span className="bg-dark-crimson text-cream px-2 py-0.5">Sold</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
